import { useState, useEffect, useCallback } from 'react';
import portalApi from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import {
  Lock, ShieldCheck, Ban, CheckCircle2, Loader2, CreditCard, FlaskConical, Play,
  PhoneCall, Scale, ListChecks, AlertTriangle, HelpCircle, Receipt, Download, DollarSign,
  Banknote, Search, Zap,
} from 'lucide-react';
import OscarVerifyProgress from './OscarVerifyProgress';

const PAY_STATUS = {
  BALANCE_VERIFIED: 'bg-slate-100 text-slate-600', AWAITING_APPROVAL: 'bg-amber-100 text-amber-700',
  APPROVED: 'bg-indigo-100 text-indigo-700', PROCESSING: 'bg-sky-100 text-sky-700',
  PAYMENT_ACCEPTED: 'bg-emerald-100 text-emerald-700', PAYMENT_STATUS_UNKNOWN: 'bg-orange-100 text-orange-700',
  FAILED: 'bg-rose-100 text-rose-700', SETTLED: 'bg-emerald-200 text-emerald-800', REVIEW_REQUIRED: 'bg-rose-100 text-rose-700',
  CANCELLED_NOT_LIVE: 'bg-slate-200 text-slate-700', CANCELLED: 'bg-slate-200 text-slate-700', REJECTED: 'bg-rose-100 text-rose-700',
};
const payBadge = (s) => PAY_STATUS[s] || 'bg-slate-200 text-slate-700';

const SECTIONS = [
  { id: 'verify', label: 'Verify Balances', icon: PhoneCall },
  { id: 'compare', label: 'Compare Balances', icon: Scale },
  { id: 'matched', label: 'Matched', icon: ListChecks },
  { id: 'mismatched', label: 'Mismatched', icon: AlertTriangle },
  { id: 'manual', label: 'Manually Verified', icon: ShieldCheck },
  { id: 'unverified', label: 'Unverified', icon: HelpCircle },
  { id: 'processing', label: 'Payment Processing', icon: CreditCard },
  { id: 'direct', label: 'Direct Payment', icon: Zap },
  { id: 'results', label: 'Payment Results', icon: Receipt },
];

const money = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);

const SummaryCard = ({ label, value, color, testid }) => (
  <Card className="p-4" data-testid={testid}>
    <div className={`text-2xl font-bold ${color || 'text-slate-900'}`}>{value}</div>
    <div className="text-xs uppercase tracking-wide text-slate-400 mt-0.5">{label}</div>
  </Card>
);

const DetailRow = ({ k, v, mono, danger, strong }) => (
  <div className="flex justify-between gap-3">
    <span className="text-slate-500">{k}</span>
    <span className={`text-right ${mono ? 'font-mono text-xs break-all' : ''} ${danger ? 'text-rose-600 font-semibold' : strong ? 'text-emerald-700 font-bold text-base' : 'text-slate-800'}`}>{v}</span>
  </div>
);

export default function OscarPaymentsTab() {
  const [section, setSection] = useState('verify');
  const [ach, setAch] = useState(null);
  const [form, setForm] = useState({ label: '', routing_number: '', account_number: '' });
  const [live, setLive] = useState(false);
  const [sim, setSim] = useState(null);
  const [simLive, setSimLive] = useState(false);
  const [busy, setBusy] = useState(null);

  const [compare, setCompare] = useState(null);
  const [matched, setMatched] = useState([]);
  const [mismatched, setMismatched] = useState([]);
  const [manualVerified, setManualVerified] = useState([]);
  const [unverified, setUnverified] = useState([]);
  const [results, setResults] = useState([]);
  const [execProgress, setExecProgress] = useState(null);
  const [matchedSummary, setMatchedSummary] = useState(null);
  const [payDialog, setPayDialog] = useState(false);
  const [payApprove, setPayApprove] = useState(false);
  const [pilotTarget, setPilotTarget] = useState(null);
  const [pilotAck, setPilotAck] = useState(false);
  const [pilotLast4, setPilotLast4] = useState('');
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolveForm, setResolveForm] = useState({ amount: '', note: '', verified: false, confirm: false });
  const [detail, setDetail] = useState(null);
  const [callerCheck, setCallerCheck] = useState(null);

  // Direct Payment — pays Oscar's announced balance for any imported member (no reconciliation).
  const [directSearch, setDirectSearch] = useState('');
  const [directMembers, setDirectMembers] = useState([]);
  const [directTarget, setDirectTarget] = useState(null);
  const [directAmount, setDirectAmount] = useState('');
  const [directLast4, setDirectLast4] = useState('');
  const [directAck, setDirectAck] = useState(false);
  const [directFullAck, setDirectFullAck] = useState(false);

  const searchDirect = async () => {
    setBusy('direct-search');
    try {
      const r = await portalApi.oscarMembers({ search: directSearch.trim(), limit: 50 });
      setDirectMembers(r.members || []);
      if (!(r.members || []).length) toast.message('No members found. Import members on the Members tab first.');
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const openDirect = (m) => {
    setDirectTarget(m); setDirectAmount(''); setDirectLast4('');
    setDirectAck(false); setDirectFullAck(false); setCallerCheck(null);
  };

  const closeDirect = () => {
    setDirectTarget(null); setDirectAmount(''); setDirectLast4('');
    setDirectAck(false); setDirectFullAck(false); setCallerCheck(null);
  };

  const runDirect = async () => {
    if (!directTarget) return;
    const amt = Number(directAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid authorization amount.'); return; }
    setBusy('direct');
    try {
      const r = await portalApi.oscarDirectPay({
        member_ref: directTarget.id, confirm_member_id: directTarget.member_id,
        authorized_amount: amt, confirm_account_last4: directLast4.trim(),
        acknowledge_real_payment: directAck, acknowledge_full_balance: directFullAck,
      });
      toast.success(`Direct payment started: ONE real ACH call for ${r.member_id}. Charging Oscar's announced balance — actual amount paid will appear in Payment Results.`);
      closeDirect();
      setSection('results');
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const checkCallers = async () => {
    setCallerCheck({ loading: true });
    try {
      const r = await portalApi.oscarVerifyCallers();
      setCallerCheck({ loading: false, ...r });
    } catch (e) { toast.error(e.message); setCallerCheck({ loading: false, error: e.message }); }
  };

  const openDetails = async (memberRef) => {
    if (!memberRef) return;
    setDetail({ loading: true });
    try {
      const d = await portalApi.oscarPaymentDetail(memberRef);
      setDetail({ loading: false, ...d });
    } catch (e) { toast.error(e.message); setDetail(null); }
  };

  const ClickableStatus = ({ status, memberRef }) => {
    if (!status) return <span className="text-slate-400 text-xs">—</span>;
    const canInspect = ['REVIEW_REQUIRED', 'PROCESSING', 'PAYMENT_STATUS_UNKNOWN'].includes(status) && memberRef;
    if (!canInspect) return <Badge className={payBadge(status)}>{status}</Badge>;
    return (
      <button type="button" onClick={() => openDetails(memberRef)} className="focus:outline-none"
        title="View payment & call details" data-testid={`status-details-btn-${memberRef}`}>
        <Badge className={`${payBadge(status)} cursor-pointer underline decoration-dotted underline-offset-2 hover:ring-2 hover:ring-slate-300`}>{status}</Badge>
      </button>
    );
  };

  const loadCompare = useCallback(async () => {
    try { const c = await portalApi.oscarCompare(); setCompare(c); setLive(c.live_payments_enabled); } catch (e) { toast.error(e.message); }
  }, []);

  const loadSection = useCallback(async (s) => {
    try {
      if (s === 'compare') { await loadCompare(); }
      else if (s === 'matched') { const r = await portalApi.oscarGroup('MATCHED'); setMatched(r.members); try { const a = await portalApi.oscarAchGet(); setAch(a); setLive(a.live_payments_enabled); } catch { /* ignore */ } }
      else if (s === 'mismatched') { const r = await portalApi.oscarGroup('MISMATCHED'); setMismatched(r.members); }
      else if (s === 'manual') { const r = await portalApi.oscarGroup('MANUALLY_VERIFIED'); setManualVerified(r.members); try { const a = await portalApi.oscarAchGet(); setAch(a); setLive(a.live_payments_enabled); } catch { /* ignore */ } }
      else if (s === 'unverified') { const r = await portalApi.oscarGroup('UNVERIFIED'); setUnverified(r.members); }
      else if (s === 'processing') { const a = await portalApi.oscarAchGet(); setAch(a); setLive(a.live_payments_enabled); try { setExecProgress(await portalApi.oscarExecutionProgress()); } catch { /* ignore */ } }
      else if (s === 'direct') { try { const a = await portalApi.oscarAchGet(); setAch(a); setLive(a.live_payments_enabled); } catch { /* ignore */ } }
      else if (s === 'results') { const r = await portalApi.oscarPayResults(); setResults(r.payments); setLive(r.live_payments_enabled); }
    } catch (e) { toast.error(e.message); }
  }, [loadCompare]);

  useEffect(() => { loadCompare(); }, [loadCompare]);
  useEffect(() => { loadSection(section); }, [section, loadSection]);

  const completedCount = results.filter((r) => ['PAYMENT_ACCEPTED', 'SETTLED'].includes(r.status)).length;

  const saveAch = async () => {
    setBusy('ach');
    try {
      await portalApi.oscarAchSet(form);
      toast.success('Agency ACH account saved (encrypted)');
      setForm({ label: '', routing_number: '', account_number: '' });
      await loadSection('processing');
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const runSim = async (scenario) => {
    setBusy('sim');
    try { setSim(await portalApi.oscarPaySimulate({ scenario, live_enabled: simLive, approved: true })); }
    catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const processBatch = async () => {
    setBusy('process');
    try {
      const r = await portalApi.oscarProcessBatch();
      toast.success(`Placing ${r.queued} real ACH payment call(s), ${r.concurrency} at a time.${r.skipped.length ? ` ${r.skipped.length} skipped.` : ''}`);
      setExecProgress(await portalApi.oscarExecutionProgress());
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const approveMember = async (m) => {
    // Explicit, operator-initiated approval (exact verified amount) via the normal approve endpoint.
    // Never fabricates a status; the backend enforces balance/eligibility and duplicate-prevention.
    const amt = m.manual_amount != null ? m.manual_amount : m.oscar_amount;
    setBusy(`approve-${m.member_ref}`);
    try {
      await portalApi.oscarApprovePayment(m.member_ref, {
        max_amount: amt,
        note: m.manual_amount != null ? 'Single-member pilot approval (manually verified)' : 'Single-member pilot approval',
      });
      toast.success(`Approved ${m.member_id} for $${Number(amt).toFixed(2)}. You can now run the pilot.`);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); loadSection(section); }
  };

  const runPilot = async () => {
    if (!pilotTarget) return;
    const m = pilotTarget;
    const amt = m._pilotAmount;
    // Require a genuinely-APPROVED record — no silent (re)approval here. If the displayed status is
    // stale, the backend 404s and the finally-block re-sync corrects the table.
    if (m.payment_status !== 'APPROVED') {
      toast.error(`Member status is ${m.payment_status || 'not approved'} — approve the member (exact match) before running the pilot.`);
      setPilotTarget(null); setPilotAck(false); setPilotLast4('');
      loadSection(section);
      return;
    }
    setBusy('pilot');
    try {
      const r = await portalApi.oscarPilotPay({
        member_ref: m.member_ref, confirm_member_id: m.member_id, confirm_amount: amt,
        confirm_account_last4: pilotLast4.trim(), acknowledge_real_payment: pilotAck,
      });
      toast.success(`Pilot started: ONE real ACH call for ${r.member_id} ($${r.amount.toFixed(2)}).`);
      setPilotTarget(null); setPilotAck(false); setPilotLast4('');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
      loadSection(section);   // always re-sync UI with backend (success OR failure) so status is never stale
    }
  };

  const submitResolve = async () => {
    if (!resolveTarget) return;
    const amt = Number(resolveForm.amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid manually verified amount.'); return; }
    setBusy('resolve');
    try {
      await portalApi.oscarResolveMismatch(resolveTarget.member_ref, {
        verified_amount: amt, source: 'oscar_portal', confirm: true, note: resolveForm.note || null,
      });
      toast.success(`Discrepancy resolved for ${resolveTarget.member_id} — marked MANUALLY_VERIFIED ($${amt.toFixed(2)}).`);
      setResolveTarget(null); setResolveForm({ amount: '', note: '', verified: false, confirm: false });
      await Promise.all([
        loadCompare(),
        portalApi.oscarGroup('MISMATCHED').then((x) => setMismatched(x.members)),
        portalApi.oscarGroup('MANUALLY_VERIFIED').then((x) => setManualVerified(x.members)),
      ]);
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const openPayDialog = async () => {
    try {
      const s = await portalApi.oscarMatchedSummary();
      setMatchedSummary(s); setPayApprove(false); setPayDialog(true);
    } catch (e) { toast.error(e.message); }
  };

  const confirmPayBatch = async () => {
    setBusy('paybatch');
    try {
      const r = await portalApi.oscarApproveMatchedBatch({
        confirm_count: matchedSummary.eligible_count,
        confirm_total_amount: matchedSummary.total_amount,
      });
      toast.success(`${r.approved} matched payments approved & authorized ($${r.total_amount.toFixed(2)}). ${r.live_payments_enabled ? '' : 'No real charge — live ACH is disabled.'}`);
      setPayDialog(false);
      await Promise.all([loadCompare(), portalApi.oscarGroup('MATCHED').then((x) => setMatched(x.members))]);
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-5" data-testid="oscar-payments-tab">
      {/* Section nav */}
      <div className="flex flex-wrap gap-1.5">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          const activeS = section === s.id;
          return (
            <button key={s.id} onClick={() => setSection(s.id)} data-testid={`pay-nav-${s.id}`}
              className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${activeS ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}>
              <Icon className="h-3.5 w-3.5" /> {s.label}
            </button>
          );
        })}
      </div>

      {/* VERIFY */}
      {section === 'verify' && (
        <>
          <Card className="p-4 text-sm text-slate-600 bg-slate-50 flex items-start gap-2">
            <PhoneCall className="h-4 w-4 text-sky-600 mt-0.5 shrink-0" />
            <span>Import members from the <strong>Members</strong> tab (<code>MemberID, Amount</code>), then run Stage 1 verification below. Verification only records &amp; transcribes the balance — it never attempts payment on the same call.</span>
          </Card>
          <OscarVerifyProgress />
        </>
      )}

      {/* COMPARE */}
      {section === 'compare' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-slate-900 flex items-center gap-2"><Scale className="h-4 w-4 text-sky-600" /> Stage 2 — Compare Balances</div>
            <Button onClick={() => loadCompare()} data-testid="compare-run-btn"><Scale className="h-4 w-4 mr-1" /> Compare Balances</Button>
          </div>
          {compare && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <SummaryCard label="Total Members" value={compare.total_members} testid="cmp-total" />
              <SummaryCard label="Matched" value={compare.matched} color="text-emerald-600" testid="cmp-matched" />
              <SummaryCard label="Mismatched" value={compare.mismatched} color="text-rose-600" testid="cmp-mismatched" />
              <SummaryCard label="Manually Verified" value={compare.manually_verified ?? 0} color="text-emerald-700" testid="cmp-manual" />
              <SummaryCard label="Unverified" value={compare.unverified} color="text-amber-600" testid="cmp-unverified" />
              <SummaryCard label="Eligible to Pay" value={compare.eligible_for_payment} color="text-indigo-600" testid="cmp-eligible" />
              <SummaryCard label="Total Matched $" value={money(compare.total_matched_amount)} color="text-emerald-700" testid="cmp-total-amount" />
            </div>
          )}
          <p className="text-[11px] text-slate-400">Exact dollar-and-cent comparison of the CSV amount vs the Whisper-verified Oscar balance. A zero balance is never eligible for payment.</p>
        </div>
      )}

      {/* MATCHED */}
      {section === 'matched' && (
        <Card className="p-0 overflow-hidden" data-testid="matched-panel">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-2">
            <span className="font-semibold text-slate-900 flex items-center gap-2"><ListChecks className="h-4 w-4 text-emerald-600" /> Matched Members ({matched.length})</span>
            <Button size="sm" onClick={openPayDialog} disabled={matched.filter((m) => m.eligible && !['APPROVED', 'PROCESSING', 'PAYMENT_ACCEPTED', 'SETTLED'].includes(m.payment_status)).length === 0} data-testid="pay-all-matched-btn">
              <DollarSign className="h-4 w-4 mr-1" /> Pay All Matched Members
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>{['Member ID', 'CSV Amount', 'Oscar (Verified)', 'Eligible', 'Payment Status', 'Pilot'].map((h) => <th key={h} className="text-left px-4 py-2 font-medium">{h}</th>)}</tr></thead>
              <tbody>
                {matched.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No matched members yet. Run Compare after verification.</td></tr>}
                {matched.map((m) => (
                  <tr key={m.member_ref} className="border-t border-slate-100" data-testid={`matched-row-${m.member_id}`}>
                    <td className="px-4 py-2 font-mono">{m.member_id}</td>
                    <td className="px-4 py-2">{money(m.provided_amount)}</td>
                    <td className="px-4 py-2 font-semibold">{money(m.oscar_amount)}</td>
                    <td className="px-4 py-2">{m.eligible ? <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="h-3 w-3 mr-1" />eligible</Badge> : <Badge className="bg-slate-100 text-slate-500">zero / excluded</Badge>}</td>
                    <td className="px-4 py-2"><ClickableStatus status={m.payment_status} memberRef={m.member_ref} /></td>
                    <td className="px-4 py-2">
                      {m.payment_status === 'APPROVED' ? (
                        <Button size="sm" variant="outline"
                          onClick={() => { setPilotTarget({ ...m, _pilotAmount: m.oscar_amount }); setPilotAck(false); }}
                          disabled={!live || !m.eligible}
                          title={!live ? 'Live ACH is disabled (OSCAR_LIVE_PAYMENTS off)' : 'Place ONE real ACH payment call for this member'}
                          data-testid={`pilot-pay-btn-${m.member_id}`}>
                          <PhoneCall className="h-3 w-3 mr-1" /> Pilot pay
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline"
                          onClick={() => approveMember(m)}
                          disabled={!m.eligible || ['PROCESSING', 'PAYMENT_ACCEPTED', 'SETTLED'].includes(m.payment_status) || busy === `approve-${m.member_ref}`}
                          title="Approve this member (exact match) so it can be piloted"
                          data-testid={`approve-btn-${m.member_id}`}>
                          <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* MISMATCHED */}
      {section === 'mismatched' && (
        <Card className="p-0 overflow-hidden" data-testid="mismatched-panel">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-2">
            <span className="font-semibold text-slate-900 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-rose-600" /> Discrepancy Table — <span className="text-rose-600">{mismatched.length} mismatched</span></span>
            <Button size="sm" variant="outline" onClick={() => portalApi.oscarDownload('/pay/discrepancies/export', 'oscar_discrepancies.csv').catch((e) => toast.error(e.message))} disabled={mismatched.length === 0} data-testid="discrepancy-export-btn">
              <Download className="h-4 w-4 mr-1" /> Export CSV
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>{['Member ID', 'Provided Balance (CSV)', 'Oscar IVR Balance (Whisper)', 'Difference', 'Resolve'].map((h) => <th key={h} className="text-left px-4 py-2 font-medium">{h}</th>)}</tr></thead>
              <tbody>
                {mismatched.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No discrepancies — every verified balance matches its CSV amount.</td></tr>}
                {mismatched.map((m) => (
                  <tr key={m.member_ref} className="border-t border-slate-100" data-testid={`mismatch-row-${m.member_id}`}>
                    <td className="px-4 py-2 font-mono">{m.member_id}</td>
                    <td className="px-4 py-2">{money(m.provided_amount)}</td>
                    <td className="px-4 py-2 font-semibold text-rose-600">{money(m.oscar_amount)}</td>
                    <td className="px-4 py-2 text-slate-600">{(m.provided_amount != null && m.oscar_amount != null) ? money(Math.abs(m.provided_amount - m.oscar_amount)) : '—'}</td>
                    <td className="px-4 py-2">
                      <Button size="sm" variant="outline"
                        onClick={() => { setResolveTarget(m); setResolveForm({ amount: m.provided_amount != null ? String(m.provided_amount) : '', note: '', verified: false, confirm: false }); }}
                        data-testid={`resolve-mismatch-btn-${m.member_id}`}>
                        <ShieldCheck className="h-3 w-3 mr-1" /> Resolve Mismatch
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 text-[11px] text-slate-400 border-t">Mismatched members are listed separately from technical failures (see the Unverified tab) so discrepancies are never confused with call errors.</div>
        </Card>
      )}

      {/* MANUALLY VERIFIED */}
      {section === 'manual' && (
        <Card className="p-0 overflow-hidden" data-testid="manual-verified-panel">
          <div className="p-4 border-b border-slate-100 font-semibold text-slate-900 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Manually Verified Members ({manualVerified.length})</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>{['Member ID', 'CSV', 'Whisper IVR', 'Verified Amount', 'Verified By', 'Reason', 'Pilot'].map((h) => <th key={h} className="text-left px-4 py-2 font-medium">{h}</th>)}</tr></thead>
              <tbody>
                {manualVerified.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No manually verified members yet. Resolve a discrepancy from the Mismatched tab.</td></tr>}
                {manualVerified.map((m) => (
                  <tr key={m.member_ref} className="border-t border-slate-100" data-testid={`manual-row-${m.member_id}`}>
                    <td className="px-4 py-2 font-mono">{m.member_id}</td>
                    <td className="px-4 py-2 text-slate-500">{money(m.provided_amount)}</td>
                    <td className="px-4 py-2 text-slate-500">{money(m.oscar_amount)}</td>
                    <td className="px-4 py-2 font-semibold text-emerald-700" data-testid={`manual-amount-${m.member_id}`}>{money(m.manual_amount)}</td>
                    <td className="px-4 py-2 text-slate-600">{m.manual_verified_by || '—'}</td>
                    <td className="px-4 py-2 text-slate-500 max-w-[220px] truncate" title={m.manual_reason}>{m.manual_reason || '—'}</td>
                    <td className="px-4 py-2 flex items-center gap-2">
                      {m.payment_status && <ClickableStatus status={m.payment_status} memberRef={m.member_ref} />}
                      {m.payment_status === 'APPROVED' ? (
                        <Button size="sm" variant="outline"
                          onClick={() => { setPilotTarget({ ...m, _pilotAmount: m.manual_amount }); setPilotAck(false); }}
                          disabled={!live || !m.eligible}
                          title={!live ? 'Live ACH is disabled (OSCAR_LIVE_PAYMENTS off)' : 'Place ONE real ACH payment call for this member at the manually verified amount'}
                          data-testid={`manual-pilot-btn-${m.member_id}`}>
                          <PhoneCall className="h-3 w-3 mr-1" /> Pilot pay
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline"
                          onClick={() => approveMember(m)}
                          disabled={!m.eligible || ['PROCESSING', 'PAYMENT_ACCEPTED', 'SETTLED'].includes(m.payment_status) || busy === `approve-${m.member_ref}`}
                          title="Approve this member (manually verified amount) so it can be piloted"
                          data-testid={`manual-approve-btn-${m.member_id}`}>
                          <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 text-[11px] text-slate-400 border-t">The original Whisper IVR result is preserved. During the real payment call Oscar's announced balance is re-checked against the manually verified amount — if it differs, the transaction STOPS and is flagged for review (the live balance is never overridden).</div>
        </Card>
      )}

      {/* UNVERIFIED */}
      {section === 'unverified' && (
        <Card className="p-0 overflow-hidden" data-testid="unverified-panel">
          <div className="p-4 border-b border-slate-100 font-semibold text-slate-900 flex items-center gap-2"><HelpCircle className="h-4 w-4 text-amber-600" /> Unverified Members ({unverified.length})</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>{['Member ID', 'CSV Amount', 'Audit Status', 'Reason'].map((h) => <th key={h} className="text-left px-4 py-2 font-medium">{h}</th>)}</tr></thead>
              <tbody>
                {unverified.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">No unverified members.</td></tr>}
                {unverified.map((m) => (
                  <tr key={m.member_ref} className="border-t border-slate-100" data-testid={`unverified-row-${m.member_id}`}>
                    <td className="px-4 py-2 font-mono">{m.member_id}</td>
                    <td className="px-4 py-2">{money(m.provided_amount)}</td>
                    <td className="px-4 py-2"><Badge className="bg-amber-100 text-amber-700">{m.audit_status || 'pending'}</Badge></td>
                    <td className="px-4 py-2 text-slate-500 text-xs">{m.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 text-[11px] text-slate-400 border-t">Technical failures (call failed, Whisper could not read the balance, review required). These are never treated as payment discrepancies.</div>
        </Card>
      )}

      {/* PROCESSING */}
      {section === 'processing' && (
        <div className="space-y-5">
          <Card className="p-6 space-y-4" data-testid="oscar-ach-settings">
            <div className="font-semibold text-slate-900 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-sky-600" /> Agency ACH Account (encrypted, admin only)</div>
            {ach?.configured ? (
              <div className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3">
                <div><strong>{ach.label}</strong> ({ach.account_type})</div>
                <div className="text-slate-500">Routing ••••{ach.routing_last4} · Account ••••{ach.account_last4}</div>
                <div className="text-[11px] text-emerald-600 mt-1">Stored encrypted — full numbers are never shown, logged, transcribed, or sent to any LLM.</div>
              </div>
            ) : <div className="text-sm text-amber-600">No agency account configured yet.</div>}
            <div className="grid md:grid-cols-3 gap-3">
              <div className="space-y-1"><Label>Label</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Agency operating account" data-testid="ach-label" /></div>
              <div className="space-y-1"><Label>Routing number (9 digits)</Label><Input value={form.routing_number} onChange={(e) => setForm({ ...form, routing_number: e.target.value })} data-testid="ach-routing" /></div>
              <div className="space-y-1"><Label>Account number</Label><Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} data-testid="ach-account" /></div>
            </div>
            <Button onClick={saveAch} disabled={busy === 'ach'} data-testid="ach-save">
              {busy === 'ach' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ShieldCheck className="h-4 w-4 mr-1" />} Save agency account
            </Button>
          </Card>

          <Card className="p-6 space-y-4" data-testid="oscar-pay-execute">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-slate-900 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-sky-600" /> Process Approved Payments (live ACH calls)</div>
              <Badge className={live ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'} data-testid="live-status-badge">{live ? 'LIVE ENABLED' : 'LIVE DISABLED'}</Badge>
            </div>
            <p className="text-sm text-slate-600">Places real Twilio ACH payment calls (up to 4 concurrent) for members who passed Stage-1 Whisper verification, matched the CSV exactly, and were explicitly approved. Oscar's balance is re-checked live on each call and payment is cancelled if it differs.</p>
            {execProgress && (
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-center" data-testid="exec-progress">
                {[['queued', execProgress.queued], ['active', execProgress.active], ['accepted', execProgress.accepted], ['unknown', execProgress.payment_status_unknown], ['failed', execProgress.failed], ['review', execProgress.review_required]].map(([k, v]) => (
                  <div key={k} className="bg-slate-50 rounded-lg p-2">
                    <div className="text-lg font-bold text-slate-900">{v ?? 0}</div>
                    <div className="text-[11px] text-slate-500 capitalize">{k}</div>
                  </div>
                ))}
              </div>
            )}
            <Button onClick={processBatch} disabled={!live || busy === 'process'} data-testid="process-batch-btn">
              {busy === 'process' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Play className="h-4 w-4 mr-1" />} Process approved payments
            </Button>
            {!live && <p className="text-[11px] text-amber-600" data-testid="live-disabled-note">Live ACH is disabled (OSCAR_LIVE_PAYMENTS off). No real payment can be placed. Use the simulator below to validate the flow safely.</p>}
          </Card>

          <Card className="p-6 space-y-4" data-testid="oscar-pay-simulator">
            <div className="font-semibold text-slate-900 flex items-center gap-2"><FlaskConical className="h-4 w-4 text-sky-600" /> ACH Payment Simulator (no real call)</div>
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => runSim('A')} disabled={busy === 'sim'} data-testid="paysim-a"><Play className="h-4 w-4 mr-1" /> Scenario A (no saved method)</Button>
              <Button size="sm" variant="outline" onClick={() => runSim('B')} disabled={busy === 'sim'} data-testid="paysim-b"><Play className="h-4 w-4 mr-1" /> Scenario B (saved method → reject)</Button>
              <Button size="sm" variant="outline" onClick={() => runSim('C')} disabled={busy === 'sim'} data-testid="paysim-c"><Play className="h-4 w-4 mr-1" /> Scenario C (direct — pays announced balance)</Button>
              <label className="flex items-center gap-2 text-sm ml-2 cursor-pointer">
                <input type="checkbox" checked={simLive} onChange={(e) => setSimLive(e.target.checked)} data-testid="paysim-live" />
                Simulate "live enabled" (full happy path)
              </label>
            </div>
            {sim && (
              <div className="border rounded-lg divide-y" data-testid="paysim-result">
                <div className="p-3 flex flex-wrap items-center gap-2">
                  <Badge className={payBadge(sim.result)}>{sim.result}</Badge>
                  {sim.reference && <span className="text-sm">Ref: <strong>{sim.reference}</strong></span>}
                  {sim.paid_amount != null && <span className="text-sm" data-testid="paysim-paid">Paid: <strong>${Number(sim.paid_amount).toFixed(2)}</strong></span>}
                </div>
                <div className="p-3 grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                  {Object.entries(sim.safety).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-1">
                      {typeof v === 'boolean'
                        ? (v ? <CheckCircle2 className="h-3 w-3 text-emerald-600" /> : <Ban className="h-3 w-3 text-slate-400" />)
                        : <span className="font-semibold">{v}</span>}
                      <span className="text-slate-600">{k.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
                <div className="p-3 space-y-1">
                  {sim.steps.map((s, i) => (
                    <div key={i} className="text-xs flex items-start gap-2">
                      <Badge className={s.sensitive ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'}>{s.sensitive ? 'SENSITIVE' : s.action}</Badge>
                      <div>
                        <div className="text-slate-500 italic">"{s.prompt}"</div>
                        <div className="text-slate-800">→ {s.rule}{s.digits ? ` (${s.digits})` : ''}{s.note ? ` — ${s.note}` : ''}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <p className="text-[11px] text-slate-400">SENSITIVE steps = bank-credential entry. Real payment calls are never recorded or transcribed, and full routing/account numbers are never stored, logged, or sent to any LLM.</p>
          </Card>
        </div>
      )}

      {/* DIRECT PAYMENT */}
      {section === 'direct' && (
        <div className="space-y-4" data-testid="direct-panel">
          <Card className="p-4 border-l-4 border-l-indigo-500 bg-indigo-50/50 flex gap-3">
            <Zap className="h-5 w-5 text-indigo-600 shrink-0 mt-0.5" />
            <div className="text-sm text-indigo-900 space-y-1">
              <div><strong>Direct Payment — no balance verification or reconciliation required.</strong> Pick any imported member, type an authorization amount, and place ONE real ACH call.</div>
              <div>Oscar's phone system only charges the <strong>full balance it announces</strong>, so the call pays THAT balance — your typed amount is kept only as your authorization/reference. The actual amount paid appears in Payment Results.</div>
              <div className="flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> The only live safeguard: the IVR-confirmed bank account must be the agency account (never another).</div>
              <Badge className={`${live ? 'bg-emerald-600' : 'bg-slate-700'} text-white`}>{live ? 'LIVE ENABLED' : 'LIVE DISABLED'}</Badge>
            </div>
          </Card>

          <Card className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Input value={directSearch} onChange={(e) => setDirectSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') searchDirect(); }}
                placeholder="Search by Member ID or name…" data-testid="direct-search-input" />
              <Button onClick={searchDirect} disabled={busy === 'direct-search'} data-testid="direct-search-btn">
                {busy === 'direct-search' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Search className="h-4 w-4 mr-1" />} Search
              </Button>
            </div>
            {directMembers.length > 0 && (
              <div className="border rounded-lg divide-y" data-testid="direct-members-list">
                {directMembers.map((m) => (
                  <div key={m.id} className="p-3 flex items-center justify-between gap-2" data-testid={`direct-member-${m.member_id}`}>
                    <div className="min-w-0">
                      <div className="font-mono font-semibold text-slate-900">{m.member_id}</div>
                      <div className="text-xs text-slate-500 truncate">{m.member_name || '—'}{m.is_test ? ' · (test data — not payable)' : ''}</div>
                    </div>
                    <Button size="sm" onClick={() => openDirect(m)} disabled={m.is_test} data-testid={`direct-select-${m.member_id}`}>
                      <Banknote className="h-4 w-4 mr-1" /> Pay directly
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[11px] text-slate-400">Members come from the <strong>Members</strong> tab import. Direct Payment ignores Verify / Compare / Matched entirely.</p>
          </Card>
        </div>
      )}

      {/* RESULTS */}
      {section === 'results' && (
        <Card className="p-0 overflow-hidden" data-testid="results-panel">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-2">
            <span className="font-semibold text-slate-900 flex items-center gap-2"><Receipt className="h-4 w-4 text-sky-600" /> Payment Results ({results.length})</span>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => portalApi.oscarDownload('/pay/receipts/export', 'oscar_payment_receipts.csv').catch((e) => toast.error(e.message))} disabled={completedCount === 0} data-testid="receipts-export-btn">
                <Receipt className="h-4 w-4 mr-1" /> Export Receipts ({completedCount})
              </Button>
              <Button size="sm" variant="outline" onClick={() => portalApi.oscarDownload('/pay/results/export', 'oscar_payment_results.csv').catch((e) => toast.error(e.message))} disabled={results.length === 0} data-testid="results-export-btn">
                <Download className="h-4 w-4 mr-1" /> Export All
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>{['Member ID', 'Authorized', 'Actual Paid', 'Status', 'Confirmation', 'Call SID', 'Date'].map((h) => <th key={h} className="text-left px-4 py-2 font-medium">{h}</th>)}</tr></thead>
              <tbody>
                {results.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No payments yet.</td></tr>}
                {results.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100" data-testid={`result-row-${r.member_id}`}>
                    <td className="px-4 py-2 font-mono">{r.member_id}</td>
                    <td className="px-4 py-2 text-slate-500">{money(r.max_amount ?? r.amount)}</td>
                    <td className="px-4 py-2">
                      {(r.actual_payment_amount ?? r.paid_amount) != null
                        ? <span className="text-base font-bold text-emerald-700" data-testid={`result-paid-${r.member_id}`}>{money(r.actual_payment_amount ?? r.paid_amount)}</span>
                        : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-2"><ClickableStatus status={r.status} memberRef={r.member_ref} /></td>
                    <td className="px-4 py-2 font-mono text-xs">{r.confirmation_ref || '—'}</td>
                    <td className="px-4 py-2 font-mono text-xs">{r.call_sid || '—'}</td>
                    <td className="px-4 py-2 text-slate-500 text-xs">{r.updated_at ? new Date(r.updated_at).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Pay All Matched confirmation */}
      <Dialog open={payDialog} onOpenChange={setPayDialog}>
        <DialogContent data-testid="pay-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-indigo-600" /> Approve matched payment batch</DialogTitle>
            <DialogDescription>Review the exact batch and total before authorizing. Only eligible, exact-match, non-zero, not-already-paid members are included.</DialogDescription>
          </DialogHeader>
          {matchedSummary && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-emerald-50 p-3"><div className="text-2xl font-bold text-emerald-700" data-testid="confirm-eligible">{matchedSummary.eligible_count}</div><div className="text-xs text-slate-500">members eligible</div></div>
                <div className="rounded-lg bg-indigo-50 p-3"><div className="text-2xl font-bold text-indigo-700" data-testid="confirm-total">${matchedSummary.total_amount.toFixed(2)}</div><div className="text-xs text-slate-500">total to be paid</div></div>
              </div>
              <div className="rounded-lg bg-slate-50 p-3">
                <div className="text-slate-500 text-xs uppercase mb-1">Authorized agency ACH</div>
                {matchedSummary.ach_account.configured
                  ? <div className="font-medium">{matchedSummary.ach_account.label} · Routing ••••{matchedSummary.ach_account.routing_last4} · Account ••••{matchedSummary.ach_account.account_last4}</div>
                  : <div className="text-rose-600">No agency ACH account configured — configure it in Payment Processing first.</div>}
              </div>
              <div className="text-xs text-slate-500 space-y-0.5">
                <div className="flex items-center gap-1"><Ban className="h-3 w-3 text-slate-400" /> Excluded — mismatched: <strong>{matchedSummary.excluded_mismatched}</strong></div>
                <div className="flex items-center gap-1"><Ban className="h-3 w-3 text-slate-400" /> Excluded — unverified: <strong>{matchedSummary.excluded_unverified}</strong></div>
                <div className="flex items-center gap-1"><Ban className="h-3 w-3 text-slate-400" /> Already paid / in-flight: <strong>{matchedSummary.already_active_or_paid}</strong></div>
              </div>
              <label className="flex items-start gap-2 text-sm cursor-pointer rounded-lg border border-slate-200 p-3">
                <input type="checkbox" checked={payApprove} onChange={(e) => setPayApprove(e.target.checked)} className="mt-0.5" data-testid="confirm-approve-check" />
                <span>I approve this exact batch of <strong>{matchedSummary.eligible_count}</strong> members for a total of <strong>${matchedSummary.total_amount.toFixed(2)}</strong>.{matchedSummary.live_payments_enabled ? '' : ' (Live ACH is disabled — this authorizes the batch but charges nothing.)'}</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayDialog(false)} data-testid="confirm-cancel-btn">Cancel</Button>
            <Button onClick={confirmPayBatch} disabled={!payApprove || busy === 'paybatch' || !matchedSummary?.eligible_count || !matchedSummary?.ach_account?.configured} data-testid="confirm-approve-btn">
              {busy === 'paybatch' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />} Approve &amp; authorize batch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Single-member PILOT confirmation — places exactly ONE real ACH call */}
      <Dialog open={!!pilotTarget} onOpenChange={(o) => { if (!o) { setPilotTarget(null); setPilotAck(false); setPilotLast4(''); setCallerCheck(null); } }}>
        <DialogContent data-testid="pilot-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PhoneCall className="h-5 w-5 text-rose-600" /> Pilot — place ONE real ACH payment call</DialogTitle>
            <DialogDescription>This places a single live payment call for exactly one member. No batch, one call at a time.</DialogDescription>
          </DialogHeader>
          {pilotTarget && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-500 uppercase">Member ID</div><div className="text-lg font-bold font-mono" data-testid="pilot-member-id">{pilotTarget.member_id}</div></div>
                <div className="rounded-lg bg-indigo-50 p-3"><div className="text-xs text-slate-500 uppercase">Approved amount</div><div className="text-lg font-bold text-indigo-700" data-testid="pilot-amount">${Number(pilotTarget._pilotAmount).toFixed(2)}</div></div>
              </div>
              <div className="rounded-lg bg-slate-50 p-3">
                <div className="text-slate-500 text-xs uppercase mb-1">Authorized agency ACH</div>
                {ach?.configured
                  ? <div className="font-medium">{ach.label} · Routing ••••{ach.routing_last4} · Account ••••{ach.account_last4}</div>
                  : <div className="text-rose-600">No agency ACH account configured — set it in Payment Processing first.</div>}
              </div>
              <div className="rounded-lg border border-slate-200 p-3 text-xs text-slate-500 space-y-0.5">
                <div className="flex items-center gap-1"><ShieldCheck className="h-3 w-3 text-emerald-500" /> Uses the agency account only — never the member's saved method.</div>
                <div className="flex items-center gap-1"><ShieldCheck className="h-3 w-3 text-emerald-500" /> Verifies Oscar announces this exact amount and account last-4 before confirming.</div>
                <div className="flex items-center gap-1"><ShieldCheck className="h-3 w-3 text-emerald-500" /> No recording/transcription of credentials; declines Oscar's save-account offer.</div>
                <div className="flex items-center gap-1"><ShieldCheck className="h-3 w-3 text-emerald-500" /> Uncertain outcomes become PAYMENT_STATUS_UNKNOWN — never auto-retried.</div>
              </div>
              {!live && <div className="rounded-lg bg-amber-50 text-amber-700 p-3 text-xs flex items-center gap-1"><Lock className="h-3 w-3" /> Live ACH is disabled. Enable OSCAR_LIVE_PAYMENTS before this can place a real call.</div>}
              <div className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs text-slate-500 uppercase">Caller-number preflight</div>
                  <Button size="sm" variant="outline" onClick={checkCallers} disabled={callerCheck?.loading} data-testid="verify-callers-btn">
                    {callerCheck?.loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <PhoneCall className="h-3 w-3 mr-1" />} Verify caller numbers
                  </Button>
                </div>
                <div className="text-[11px] text-slate-400 mt-1">Read-only Twilio check (no call). A From number that isn't purchased or a verified caller ID fails with error 21210.</div>
                {callerCheck && !callerCheck.loading && !callerCheck.error && (
                  <div className="mt-2 space-y-1" data-testid="caller-check-results">
                    {(callerCheck.numbers || []).map((n, i) => (
                      <div key={i} className="flex items-center justify-between text-xs rounded bg-slate-50 px-2 py-1">
                        <span className="font-mono">{n.number_masked}</span>
                        <span className={n.usable ? 'text-emerald-600 font-semibold' : 'text-rose-600 font-semibold'}>{n.status}</span>
                      </div>
                    ))}
                    <div className={`text-xs font-semibold mt-1 ${callerCheck.all_usable ? 'text-emerald-600' : 'text-rose-600'}`} data-testid="caller-check-verdict">
                      {callerCheck.all_usable ? '✓ All caller numbers usable' : '✗ Fix caller numbers before placing a call'}
                    </div>
                  </div>
                )}
                {callerCheck?.error && <div className="text-xs text-rose-600 mt-2">{callerCheck.error}</div>}
              </div>
              <div className="rounded-lg border border-slate-200 p-3">
                <label className="text-xs text-slate-500 uppercase mb-1 block">Confirm agency account last 4 digits</label>
                <input type="text" inputMode="numeric" maxLength={4} value={pilotLast4}
                  onChange={(e) => setPilotLast4(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="####" className="w-28 font-mono text-lg tracking-widest border rounded px-2 py-1"
                  data-testid="pilot-last4-input" />
                <div className="text-[11px] text-slate-400 mt-1">Must match the authorized agency account shown above.</div>
              </div>
              <label className="flex items-start gap-2 cursor-pointer rounded-lg border border-rose-200 bg-rose-50 p-3">
                <input type="checkbox" checked={pilotAck} onChange={(e) => setPilotAck(e.target.checked)} className="mt-0.5" data-testid="pilot-ack-check" />
                <span>I confirm this is a <strong>REAL ACH transaction</strong> and explicitly authorize ONE payment of <strong>${Number(pilotTarget._pilotAmount).toFixed(2)}</strong> for member <strong>{pilotTarget.member_id}</strong>.</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPilotTarget(null); setPilotAck(false); setPilotLast4(''); }} data-testid="pilot-cancel-btn">Cancel</Button>
            <Button onClick={runPilot} disabled={!pilotAck || pilotLast4.length !== 4 || !live || busy === 'pilot' || !ach?.configured} className="bg-rose-600 hover:bg-rose-700" data-testid="pilot-confirm-btn">
              {busy === 'pilot' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <PhoneCall className="h-4 w-4 mr-1" />} Place ONE real call
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Direct Payment confirmation — places ONE real ACH call; pays Oscar's announced balance */}
      <Dialog open={!!directTarget} onOpenChange={(o) => { if (!o) closeDirect(); }}>
        <DialogContent data-testid="direct-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Zap className="h-5 w-5 text-indigo-600" /> Direct payment — place ONE real ACH call</DialogTitle>
            <DialogDescription>Pays the full balance Oscar announces on the call. Your typed amount is recorded as your authorization only.</DialogDescription>
          </DialogHeader>
          {directTarget && (
            <div className="space-y-3 text-sm">
              <div className="rounded-lg bg-slate-50 p-3">
                <div className="text-xs text-slate-500 uppercase">Member</div>
                <div className="text-lg font-bold font-mono" data-testid="direct-member-id">{directTarget.member_id}</div>
                <div className="text-xs text-slate-500">{directTarget.member_name || '—'}</div>
              </div>
              <div className="rounded-lg border border-slate-200 p-3">
                <Label>Authorization amount ($)</Label>
                <Input type="number" step="0.01" min="0" value={directAmount}
                  onChange={(e) => setDirectAmount(e.target.value)} placeholder="e.g. 50.00"
                  data-testid="direct-amount-input" />
                <div className="text-[11px] text-amber-600 mt-1">Recorded as your authorization/reference only. Oscar charges the balance it announces, which may be higher or lower.</div>
              </div>
              <div className="rounded-lg bg-slate-50 p-3">
                <div className="text-slate-500 text-xs uppercase mb-1">Authorized agency ACH</div>
                {ach?.configured
                  ? <div className="font-medium">{ach.label} · Routing ••••{ach.routing_last4} · Account ••••{ach.account_last4}</div>
                  : <div className="text-rose-600">No agency ACH account configured — set it in Payment Processing first.</div>}
              </div>
              <div className="rounded-lg border border-slate-200 p-3 text-xs text-slate-500 space-y-0.5">
                <div className="flex items-center gap-1"><ShieldCheck className="h-3 w-3 text-emerald-500" /> Uses the agency account only — never the member's saved method.</div>
                <div className="flex items-center gap-1"><ShieldCheck className="h-3 w-3 text-emerald-500" /> Verifies the IVR-confirmed account is the agency account before authorizing.</div>
                <div className="flex items-center gap-1"><ShieldCheck className="h-3 w-3 text-emerald-500" /> No recording/transcription of credentials; declines Oscar's save-account offer.</div>
                <div className="flex items-center gap-1"><ShieldCheck className="h-3 w-3 text-emerald-500" /> Stops without paying if the announced amount is unclear or above the agency safety maximum.</div>
                <div className="flex items-center gap-1"><ShieldCheck className="h-3 w-3 text-emerald-500" /> Uncertain outcomes become PAYMENT_STATUS_UNKNOWN — never auto-retried.</div>
              </div>
              {!live && <div className="rounded-lg bg-amber-50 text-amber-700 p-3 text-xs flex items-center gap-1"><Lock className="h-3 w-3" /> Live ACH is disabled. Enable OSCAR_LIVE_PAYMENTS before this can place a real call.</div>}
              <div className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs text-slate-500 uppercase">Caller-number preflight</div>
                  <Button size="sm" variant="outline" onClick={checkCallers} disabled={callerCheck?.loading} data-testid="direct-verify-callers-btn">
                    {callerCheck?.loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <PhoneCall className="h-3 w-3 mr-1" />} Verify caller numbers
                  </Button>
                </div>
                {callerCheck && !callerCheck.loading && !callerCheck.error && (
                  <div className="mt-2 space-y-1" data-testid="direct-caller-check-results">
                    {(callerCheck.numbers || []).map((n, i) => (
                      <div key={i} className="flex items-center justify-between text-xs rounded bg-slate-50 px-2 py-1">
                        <span className="font-mono">{n.number_masked}</span>
                        <span className={n.usable ? 'text-emerald-600 font-semibold' : 'text-rose-600 font-semibold'}>{n.status}</span>
                      </div>
                    ))}
                    <div className={`text-xs font-semibold mt-1 ${callerCheck.all_usable ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {callerCheck.all_usable ? '✓ All caller numbers usable' : '✗ Fix caller numbers before placing a call'}
                    </div>
                  </div>
                )}
                {callerCheck?.error && <div className="text-xs text-rose-600 mt-2">{callerCheck.error}</div>}
              </div>
              <div className="rounded-lg border border-slate-200 p-3">
                <label className="text-xs text-slate-500 uppercase mb-1 block">Confirm agency account last 4 digits</label>
                <input type="text" inputMode="numeric" maxLength={4} value={directLast4}
                  onChange={(e) => setDirectLast4(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="####" className="w-28 font-mono text-lg tracking-widest border rounded px-2 py-1"
                  data-testid="direct-last4-input" />
                <div className="text-[11px] text-slate-400 mt-1">Must match the authorized agency account shown above.</div>
              </div>
              <label className="flex items-start gap-2 cursor-pointer rounded-lg border border-indigo-200 bg-indigo-50 p-3">
                <input type="checkbox" checked={directFullAck} onChange={(e) => setDirectFullAck(e.target.checked)} className="mt-0.5" data-testid="direct-fullbalance-check" />
                <span>I understand Oscar will charge <strong>the balance it announces</strong> (which may differ from ${Number(directAmount || 0).toFixed(2)}), and the actual amount paid will be reported.</span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer rounded-lg border border-rose-200 bg-rose-50 p-3">
                <input type="checkbox" checked={directAck} onChange={(e) => setDirectAck(e.target.checked)} className="mt-0.5" data-testid="direct-ack-check" />
                <span>I confirm this is a <strong>REAL ACH transaction</strong> and authorize ONE direct payment for member <strong>{directTarget.member_id}</strong>.</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeDirect} data-testid="direct-cancel-btn">Cancel</Button>
            <Button onClick={runDirect} className="bg-indigo-600 hover:bg-indigo-700" data-testid="direct-confirm-btn"
              disabled={!directAck || !directFullAck || directLast4.length !== 4 || !Number(directAmount) || !live || busy === 'direct' || !ach?.configured}>
              {busy === 'direct' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <PhoneCall className="h-4 w-4 mr-1" />} Place ONE real call
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolve Mismatch — document a manually verified discrepancy */}
      <Dialog open={!!resolveTarget} onOpenChange={(o) => { if (!o) { setResolveTarget(null); } }}>
        <DialogContent data-testid="resolve-mismatch-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-600" /> Resolve balance discrepancy</DialogTitle>
            <DialogDescription>Document an administrator's manual verification. The original Whisper IVR result is preserved for the audit trail.</DialogDescription>
          </DialogHeader>
          {resolveTarget && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-500 uppercase">Member ID</div><div className="font-bold font-mono" data-testid="resolve-member-id">{resolveTarget.member_id}</div></div>
                <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-500 uppercase">CSV amount</div><div className="font-bold">{money(resolveTarget.provided_amount)}</div></div>
                <div className="rounded-lg bg-rose-50 p-3"><div className="text-xs text-slate-500 uppercase">Whisper IVR</div><div className="font-bold text-rose-600">{money(resolveTarget.oscar_amount)}</div></div>
              </div>
              <div className="rounded-lg border border-slate-200 p-3 text-xs text-slate-500">
                Difference: <strong className="text-slate-700">{(resolveTarget.provided_amount != null && resolveTarget.oscar_amount != null) ? money(Math.abs(resolveTarget.provided_amount - resolveTarget.oscar_amount)) : '—'}</strong>
              </div>
              <label className="flex items-start gap-2 cursor-pointer rounded-lg border border-slate-200 p-3">
                <input type="checkbox" checked={resolveForm.verified} onChange={(e) => setResolveForm({ ...resolveForm, verified: e.target.checked })} className="mt-0.5" data-testid="resolve-verified-check" />
                <span>I independently <strong>verified this balance against the Oscar broker portal</strong>.</span>
              </label>
              <div className="space-y-1">
                <Label>Manually verified amount ($)</Label>
                <Input type="number" step="0.01" min="0" value={resolveForm.amount} onChange={(e) => setResolveForm({ ...resolveForm, amount: e.target.value })} placeholder="e.g. 25.27" data-testid="resolve-amount-input" />
              </div>
              <div className="space-y-1">
                <Label>Supporting note (optional)</Label>
                <Input value={resolveForm.note} onChange={(e) => setResolveForm({ ...resolveForm, note: e.target.value })} placeholder="Reason / where you verified it" data-testid="resolve-note-input" />
              </div>
              <label className="flex items-start gap-2 cursor-pointer rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <input type="checkbox" checked={resolveForm.confirm} onChange={(e) => setResolveForm({ ...resolveForm, confirm: e.target.checked })} className="mt-0.5" data-testid="resolve-confirm-check" />
                <span>I confirm this manually verified amount and understand it will be recorded as <strong>MANUALLY_VERIFIED</strong> (the Whisper result is kept).</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveTarget(null)} data-testid="resolve-cancel-btn">Cancel</Button>
            <Button onClick={submitResolve} disabled={!resolveForm.verified || !resolveForm.confirm || !Number(resolveForm.amount) || busy === 'resolve'} className="bg-emerald-600 hover:bg-emerald-700" data-testid="resolve-submit-btn">
              {busy === 'resolve' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />} Save manual verification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment investigation — read-only, built from existing payment + call records */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="payment-details-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Receipt className="h-5 w-5 text-slate-700" /> Payment investigation</DialogTitle>
            <DialogDescription>Read-only view built from existing payment and call records. No retry action is available by design — verify the previous outcome before any new attempt.</DialogDescription>
          </DialogHeader>
          {detail?.loading && <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}
          {detail && !detail.loading && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-500 uppercase">Member ID</div><div className="font-bold font-mono" data-testid="detail-member-id">{detail.member_id}</div></div>
                <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-500 uppercase">Balance</div><div className="font-bold">{money(detail.balance_amount)}</div></div>
              </div>

              <div>
                <div className="text-xs font-semibold uppercase text-slate-500 mb-2">Payment records / status history ({(detail.payments || []).length})</div>
                {(detail.payments || []).length === 0 && <div className="text-slate-400 text-xs">No payment records.</div>}
                <div className="space-y-2">
                  {(detail.payments || []).map((p) => (
                    <div key={p.id} className="rounded-lg border border-slate-200 p-3 space-y-1" data-testid={`detail-payment-${p.id}`}>
                      <div className="flex items-center justify-between">
                        <Badge className={payBadge(p.status)}>{p.status}</Badge>
                        <span className="text-xs text-slate-500">{p.updated_at ? new Date(p.updated_at).toLocaleString() : (p.created_at ? new Date(p.created_at).toLocaleString() : '—')}</span>
                      </div>
                      <DetailRow k="Authorized amount (reference)" v={money(p.authorized_amount ?? p.amount ?? p.max_amount)} />
                      <DetailRow k="Actual amount paid" v={(p.actual_payment_amount ?? p.paid_amount) != null ? money(p.actual_payment_amount ?? p.paid_amount) : '—'} strong />
                      <DetailRow k="Authorized at" v={p.authorized_at ? new Date(p.authorized_at).toLocaleString() : '—'} />
                      <DetailRow k="Reason for review / note" v={p.note || 'Reason not recorded'} />
                      <DetailRow k="Confirmation reference" v={p.confirmation_ref || 'None received'} />
                      <DetailRow k="Twilio Call SID" v={p.call_sid || '—'} mono />
                      <DetailRow k="Call status" v={p.call_status || '—'} />
                      {p.twilio_error && (
                        <DetailRow k="Twilio error" danger
                          v={`${p.twilio_error.code ? `[${p.twilio_error.code}] ` : ''}${p.twilio_error.message || '—'}${p.twilio_error.stage ? ` (stage: ${p.twilio_error.stage})` : ''}`} />
                      )}
                      <DetailRow k="Approved by" v={p.approved_by_name ? `${p.approved_by_name}${p.approved_at ? ' · ' + new Date(p.approved_at).toLocaleString() : ''}` : '—'} />
                      <DetailRow k="Source" v={p.source || '—'} />
                      <DetailRow k="Created" v={p.created_at ? new Date(p.created_at).toLocaleString() : '—'} />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-xs font-semibold uppercase text-slate-500 mb-2">Payment call records ({(detail.payment_calls || []).length})</div>
                {(detail.payment_calls || []).length === 0 && <div className="text-slate-400 text-xs">No payment call placed for this member.</div>}
                <div className="space-y-3">
                  {(detail.payment_calls || []).map((c) => (
                    <div key={c.id} className="rounded-lg border border-slate-200 p-3 space-y-1" data-testid={`detail-call-${c.id}`}>
                      <div className="flex items-center justify-between">
                        <Badge className={payBadge(c.result)}>{c.result || c.state}</Badge>
                        <span className="text-xs text-slate-500">{c.ended_at ? new Date(c.ended_at).toLocaleString() : (c.created_at ? new Date(c.created_at).toLocaleString() : '—')}</span>
                      </div>
                      <DetailRow k="Twilio Call SID" v={c.call_sid || '—'} mono />
                      <DetailRow k="Current call status" v={c.twilio_status || c.state || '—'} />
                      <DetailRow k="Final ACH authorization sent" v={c.authorization_sent ? 'YES' : 'No'} danger={!!c.authorization_sent} />
                      {c.twilio_error && (
                        <>
                          <DetailRow k="Twilio error code" v={c.twilio_error.code != null ? String(c.twilio_error.code) : '—'} danger mono />
                          <DetailRow k="Twilio error message" v={c.twilio_error.message || '—'} danger />
                          <DetailRow k="Failure stage" v={c.twilio_error.stage || '—'} />
                        </>
                      )}
                      <DetailRow k="Confirmation reference received" v={c.confirmation_ref ? `Yes · ${c.confirmation_ref}` : 'No'} />
                      <DetailRow k="Agency account (last 4)" v={c.account_last4 ? `••${c.account_last4}` : '—'} mono />
                      <DetailRow k="Approved amount" v={money(c.approved_amount)} />
                      <DetailRow k="IVR turns" v={c.turns != null ? String(c.turns) : '—'} />
                      <DetailRow k="Duration" v={c.duration_seconds != null ? `${c.duration_seconds}s` : '—'} />
                      <DetailRow k="Last attempted action / outcome" v={c.note || 'Reason not recorded'} />
                      {(c.steps || []).length > 0 && (
                        <div className="mt-2">
                          <div className="text-xs font-semibold text-slate-500 mb-1">IVR step timeline</div>
                          <div className="space-y-1 max-h-48 overflow-y-auto">
                            {(c.steps || []).map((s, i) => (
                              <div key={i} className="text-xs rounded bg-slate-50 p-2">
                                <div className="flex justify-between text-slate-400"><span>{s.rule || s.action || '—'}</span><span>{s.at ? new Date(s.at).toLocaleTimeString() : ''}</span></div>
                                <div className="text-slate-600 truncate" title={s.prompt}>“{s.prompt || '—'}”</div>
                                {s.note && <div className="text-slate-500">{s.note}</div>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)} data-testid="detail-close-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
